import { Link } from "react-router-dom";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const SECTIONS = [
  {
    id: "informational-use",
    h: "Informational use only",
    p: [
      "melbiwellness.com is informational and does not provide medical advice. Visiting this site does not create a clinician-patient relationship. Statements have not been evaluated by the U.S. Food and Drug Administration and are not intended to diagnose, treat, cure, or prevent any disease.",
    ],
  },
  {
    id: "clinician-review",
    h: "Clinician review and eligibility",
    p: [
      "Treatment is only available when clinically appropriate after an online health intake and review by a licensed healthcare provider. Approval is never guaranteed and not all patients qualify. A provider may approve, deny, request more information, or require a brief live visit. Refills require continued clinical appropriateness review.",
    ],
  },
  {
    id: "compounded-medications",
    h: "Compounded medications",
    p: [
      "Compounded medications, if prescribed, are not FDA-approved. The FDA does not review compounded medications for safety, effectiveness, or quality before they are marketed. Compounded medications are prepared by a state-licensed compounding pharmacy specifically for the individual patient, and are not the same as, equivalent to, or interchangeable with FDA-approved brand-name medications.",
    ],
  },
  {
    id: "pharmacy-fulfillment",
    h: "Pharmacy fulfillment",
    p: [
      "If prescribed, medication is dispensed by a state-licensed pharmacy and shipped directly to the patient. Melbi does not dispense or manufacture medication. Certain medications, formulations, or fulfillment options may vary depending on state-specific pharmacy regulations and dispensing restrictions.",
    ],
  },
  {
    id: "individual-results",
    h: "Individual results and packaging",
    p: [
      "Individual results vary and are not guaranteed. Wellness outcomes depend on a wide range of factors. Product packaging shown on this site is for illustration only, and actual product packaging may appear differently than shown.",
    ],
  },
];

export default function MedicalDisclosures() {
  useSEO({
    title: "Medical Disclosures | Melbi Wellness",
    description:
      "Important medical disclosures for Melbi Wellness: clinician review, compounded medications, pharmacy fulfillment, and individual results.",
    url: "https://melbiwellness.com/medical-disclosures",
    jsonLd: [breadcrumbSchema([{ name: "Home", url: "https://melbiwellness.com/" }, { name: "Medical Disclosures", url: "https://melbiwellness.com/medical-disclosures" }])],
  });

  return (
    <div data-testid="medical-disclosures-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 md:pt-14">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">Legal</p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#2C2B29]">
        Medical Disclosures
      </h1>
      <p className="mt-4 text-[#5E5C58] leading-relaxed">
        General information about how Melbi care works. Treatment-specific safety and pricing
        details appear on each treatment page.
      </p>

      <div className="mt-10 space-y-8">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} data-testid={`disclosure-${s.id}`} className="scroll-mt-28">
            <h2 className="font-display text-xl md:text-2xl font-semibold text-[#2C2B29]">{s.h}</h2>
            {s.p.map((t) => (
              <p key={t} className="mt-2 text-sm md:text-base text-[#5E5C58] leading-relaxed">{t}</p>
            ))}
          </section>
        ))}
        <section id="subscriptions" data-testid="disclosure-subscriptions" className="scroll-mt-28">
          <h2 className="font-display text-xl md:text-2xl font-semibold text-[#2C2B29]">Subscriptions and cancellation</h2>
          <p className="mt-2 text-sm md:text-base text-[#5E5C58] leading-relaxed">
            Billing intervals, renewals, cancellations, and refunds are described in our{" "}
            <Link to="/legal/refund" className="font-semibold text-[#2C2B29] underline decoration-[#C96A4E] underline-offset-4 hover:text-[#C96A4E]">
              Cancellation, Refund, and Subscription Policy
            </Link>
            .
          </p>
        </section>
        <section id="service-areas" data-testid="disclosure-service-areas" className="scroll-mt-28">
          <h2 className="font-display text-xl md:text-2xl font-semibold text-[#2C2B29]">Service areas and pharmacy partners</h2>
          <p className="mt-2 text-sm md:text-base text-[#5E5C58] leading-relaxed">
            Jurisdiction and partner pharmacy information is shown in the footer of every page. See{" "}
            <Link to="/service-areas" className="font-semibold text-[#2C2B29] underline decoration-[#C96A4E] underline-offset-4 hover:text-[#C96A4E]">
              Service Areas
            </Link>{" "}
            for state availability.
          </p>
        </section>
      </div>
    </div>
  );
}
